import { AlertTriangle, TrendingDown, Wallet } from "lucide-react";
import { TableSkeleton, useSlowEnoughToShow } from "../components/Skeleton";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import type { PortfolioClient } from "../types";
import { formatCredits, formatDate } from "../utils";

/**
 * Prismic's own view across every client. Super admin only.
 *
 * KD is front and centre here, which looks like it contradicts the credits-first
 * rule — it doesn't. That rule is about CLIENT-facing screens, where showing a
 * negotiated rate is a leak waiting to happen. This screen has no client on it.
 * It is the commercial view, and the commercial question is money.
 *
 * The number the page is built around is UNUSED VALUE. Credits a client paid for
 * and did not commission are not savings — they are work you were contracted to do
 * and were never asked for, and they are what a renewal conversation turns on.
 */

type Health = { label: string; tone: "good" | "watch" | "risk"; why: string };

/**
 * Burn is judged against the contract SO FAR, not the calendar month. One quiet
 * month is a holiday; a quiet quarter is a client who has stopped thinking about
 * you. Month one is exempt — nobody has had time to spend anything yet.
 */
function health(client: PortfolioClient): Health {
  if (client.monthsElapsed <= 1) return { label: "New", tone: "good", why: "First month — too early to read" };
  const pct = Math.round(client.burnRate * 100);
  if (pct >= 70) return { label: "Healthy", tone: "good", why: `${pct}% of granted credits commissioned` };
  if (pct >= 40) return { label: "Watch", tone: "watch", why: `Only ${pct}% commissioned after ${client.monthsElapsed} months` };
  return { label: "At risk", tone: "risk", why: `${pct}% commissioned after ${client.monthsElapsed} months` };
}

const kd = (value: number) => `${formatCredits(value)} KD`;

export default function PortfolioPage({ month }: { month: string }) {
  const [clients, setClients] = useState<PortfolioClient[] | null>(null);

  useEffect(() => {
    let live = true;
    dashboardApi.portfolio(month)
      .then((data) => { if (live) setClients(data.clients); })
      .catch((error) => toast.error(error instanceof Error ? error.message : "Could not load the portfolio."));
    return () => { live = false; };
  }, [month]);

  const showSkeleton = useSlowEnoughToShow(!clients);

  if (!clients) return showSkeleton
    ? <div className="page-stack"><TableSkeleton rows={5} label="Loading the client portfolio" /></div>
    : null;

  const annualValue = clients.reduce((total, c) => total + c.annualValueKd, 0);
  const unusedValue = clients.reduce((total, c) => total + c.unusedValueKd, 0);
  const atRisk = clients.filter((c) => health(c).tone === "risk");
  // Worst first. The point of the page is the clients you are not thinking about.
  const ordered = [...clients].sort((a, b) => a.burnRate - b.burnRate);

  return (
    <div className="page-stack">
      <section className="metric-grid">
        <article className="metric-card metric-primary">
          <div className="metric-icon"><Wallet size={21} /></div>
          <span>Retainer under management</span>
          <strong>{kd(annualValue)}</strong>
          <small>{clients.length} client{clients.length === 1 ? "" : "s"}, annualised</small>
        </article>
        <article className="metric-card">
          <div className="metric-icon amber"><TrendingDown size={21} /></div>
          <span>Contracted and not commissioned</span>
          <strong>{kd(unusedValue)}</strong>
          <small>Work they are owed and have not asked for</small>
        </article>
        <article className="metric-card">
          <div className="metric-icon neutral"><AlertTriangle size={21} /></div>
          <span>Clients at risk</span>
          <strong>{atRisk.length}</strong>
          <small>{atRisk.length ? atRisk.map((c) => c.name).join(", ") : "Nobody under 40% burn"}</small>
        </article>
      </section>

      <section className="panel">
        <div className="panel-header compact">
          <div>
            <span className="section-eyebrow">Every client</span>
            <h3>Least engaged first</h3>
          </div>
          {/* Short on purpose: panel-header lays this out on one line beside the
              title, and the longer wording overflowed the panel on a 1440 screen. */}
          <span className="portfolio-note">Contract to date</span>
        </div>
        <div className="responsive-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th><th>Health</th><th className="numeric">Burn</th>
                <th className="numeric">Unused</th><th className="numeric">This month</th>
                <th className="numeric">Open</th><th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((client) => {
                const state = health(client);
                return (
                  <tr key={client.id}>
                    <td data-label="Client">
                      <strong>{client.name}</strong>
                      <span>{formatCredits(client.monthlyCredits)} cr/month · {kd(client.creditValueKd)} per credit</span>
                    </td>
                    <td data-label="Health">
                      <span className={`status-pill ${state.tone === "good" ? "approved" : state.tone === "watch" ? "pending" : "rejected"}`}>{state.label}</span>
                      <span className="portfolio-why">{state.why}</span>
                    </td>
                    <td data-label="Burn" className="numeric">
                      <strong>{Math.round(client.burnRate * 100)}%</strong>
                      <span>{formatCredits(client.spentToDate)} of {formatCredits(client.grantedToDate)}</span>
                    </td>
                    <td data-label="Unused" className="numeric">
                      <strong>{kd(client.unusedValueKd)}</strong>
                      <span>{formatCredits(client.unusedCredits)} credits</span>
                    </td>
                    <td data-label="This month" className="numeric">
                      {formatCredits(client.thisMonth.spent)} spent
                      <span>{formatCredits(client.thisMonth.available)} available</span>
                    </td>
                    <td data-label="Open" className="numeric">{client.openRequests}</td>
                    <td data-label="Last activity">{client.lastActivity ? formatDate(client.lastActivity) : "Never"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
